/**
 * Typed form for legacy (v1) encounter types.
 *
 * Reachable only via edit on an existing encounter that carries one of the
 * v1 tags (`outreach`, `initial`, `check-in-evaluation`, `follow-up`).
 * The Type step is read-only with the legacy options selector; both notes
 * variants are visible (legacy types historically supported both general
 * and clinical impressions); Details show the billable option.
 *
 * @agentic connections
 */

import { Alert, Skeleton, Stack } from "@mantine/core";
import { IconAlertTriangle } from "@tabler/icons-react";
import { useForm } from "@mantine/form";
import { useMedplum } from "@medplum/react";
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import type { Encounter, Patient, PractitionerRole } from "@medplum/fhirtypes";

import { PRACTITIONER_ROLE_PAGINATION_COUNT } from "@benchmark/core/constants";
import { CONFIG } from "@/app/config";
import { FormStep, SteppedForm } from "@benchmark/ui/components/atomic";
import { useFormProtection } from "@benchmark/ui/hooks";
import { useFormSubmission } from "@benchmark/ui/hooks";
import { useStableParams } from "@/hooks/common/useStableParams";
import { usePatientResource } from "@/hooks/shared/useFHIRResource";
import { usePractitionerRoleOptions } from "@/hooks/shared/useFHIROptions";
import { encounterToForm } from "@benchmark/core/adapters";
import { legacyEncounterFromBundleInput } from "@benchmark/core/adapters";
import { loadEncounterFormDataContext } from "@benchmark/core/services";
import type { LegacyEncounterFormData } from "@benchmark/core/types";

import { ClinicalNotesFields } from "../Encounter.clinicalNotesFields";
import { EncounterTypeSection } from "../sections/EncounterTypeSection";
import type { EncounterFormRef } from "../EncounterFormShell";
import { EncounterDetailsStep } from "../shared/EncounterDetailsStep";
import { GeneralNoteStep } from "../shared/GeneralNoteStep";
import {
  buildAllTypeOptions,
  buildBillableOptions,
  buildDirectionOptions,
  buildMethodOptions,
} from "../shared/encounterIcons";
import { useLegacyEncounterSubmit } from "./useLegacyEncounterSubmit";

const METHOD_OPTIONS = buildMethodOptions();
const DIRECTION_OPTIONS = buildDirectionOptions();
const BILLABLE_OPTIONS = buildBillableOptions(true);
const ALL_TYPE_OPTIONS = buildAllTypeOptions();

/**
 * Props for `LegacyEncounterReadOnlyForm`. `encounter` is required.
 *
 * @agentic connections
 */
export type LegacyEncounterReadOnlyFormProps = {
  patientId?: string;
  encounter: Encounter;
  onSuccess?: (encounterId: string) => void;
  onCancel?: () => void;
};

const LEGACY_INITIAL: LegacyEncounterFormData = {
  "encounter-type": "follow-up",
  period: { start: "", end: "" },
  "encounter-method": "",
  "encounter-inout": "",
  "encounter-billable": "",
  comment: "",
  selectedQuestionnaires: [],
  questionnaireResponses: {},
};

/**
 * Read-only-type form for legacy encounters.
 *
 * @agentic connections
 */
export const LegacyEncounterReadOnlyForm = forwardRef<
  EncounterFormRef,
  LegacyEncounterReadOnlyFormProps
>(function LegacyEncounterReadOnlyForm(
  { patientId: propPatientId, encounter, onSuccess, onCancel },
  ref,
) {
  const medplum = useMedplum();
  // useMedplumProfile is consumed inside useLegacyEncounterSubmit; calling it
  // here would be redundant. We still rely on it indirectly via that hook.
  const { patientId: urlPatientId } = useStableParams();
  const effectivePatientId = propPatientId || urlPatientId;
  const { data: patientResource } = usePatientResource(effectivePatientId);
  const patient = patientResource as Patient | undefined;

  const { data: practitionerRoleResources } = usePractitionerRoleOptions({
    organization: `Organization/${CONFIG.VITE_BENCHMARK_ORGANIZATION_ID}`,
    active: "true",
    _include: "PractitionerRole:practitioner",
    _count: PRACTITIONER_ROLE_PAGINATION_COUNT,
  });
  const practitionerRoles = (practitionerRoleResources ??
    []) as PractitionerRole[];

  const form = useForm<LegacyEncounterFormData>({
    initialValues: LEGACY_INITIAL,
  });

  const { checkProtection, markAsClean, ConfirmationModal } = useFormProtection(
    form,
    {
      enabled: true,
      blockNavigation: true,
      confirmationMessage:
        "You have unsaved changes. Are you sure you want to close without saving?",
    },
  );

  useImperativeHandle(ref, () => ({ checkProtection, markAsClean }), [
    checkProtection,
    markAsClean,
  ]);

  const hydratedRef = useRef(false);
  const [isLoadingEdit, setIsLoadingEdit] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [originalFormData, setOriginalFormData] = useState<
    LegacyEncounterFormData | undefined
  >(undefined);

  useEffect(() => {
    if (!patient) return;
    if (hydratedRef.current) return;
    hydratedRef.current = true;

    let cancelled = false;
    (async () => {
      try {
        const context = await loadEncounterFormDataContext(
          medplum,
          encounter,
          patient,
          { throwOnError: true },
        );
        const flat = encounterToForm(context);
        const typed = legacyEncounterFromBundleInput(flat);
        if (cancelled || !typed) return;
        form.setValues(typed);
        form.setInitialValues(typed);
        form.resetDirty();
        setOriginalFormData(typed);
      } catch {
        console.error("Failed to hydrate legacy encounter");
        if (!cancelled) setLoadError(true);
      } finally {
        if (!cancelled) setIsLoadingEdit(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [patient, encounter, medplum, form]);

  const handleSubmit = useLegacyEncounterSubmit({
    patient,
    existingEncounter: encounter,
    originalFormData,
    practitionerRoles,
    onSuccess,
    onSubmitted: markAsClean,
  });

  const { createSubmissionHandler, isSubmitting } = useFormSubmission();

  const steps = useMemo(
    () => [
      {
        id: "type",
        title: "Type",
        subtitle: "Interaction type. (Read-Only)",
        readOnly: true,
        requiredFields: ["encounter-type"] as string[],
      },
      {
        id: "notes",
        title: "Notes",
        subtitle: "Document the encounter",
      },
      {
        id: "details",
        title: "Details",
        subtitle: "Review and confirm the interaction details.",
        requiredFields: [
          "period.start",
          "period.end",
          "encounter-method",
          "encounter-inout",
          "encounter-billable",
        ] as string[],
      },
    ],
    [],
  );

  if (loadError) {
    return (
      <div data-testid="legacy-encounter-form">
        <Alert
          color="red"
          icon={<IconAlertTriangle size={18} />}
          title="Couldn't load this interaction"
          data-testid="legacy-encounter-load-error"
        >
          We couldn&apos;t load the saved details for this interaction. Refresh
          the page to try again — previous documentation has not been changed.
        </Alert>
        <ConfirmationModal />
      </div>
    );
  }

  if (!patient || isLoadingEdit) {
    return (
      <div data-testid="legacy-encounter-form">
        <Stack gap="md">
          <Skeleton height={32} width="40%" />
          <Skeleton height={36} />
          <Skeleton height={44} width="200px" />
        </Stack>
        <ConfirmationModal />
      </div>
    );
  }

  return (
    <div data-testid="legacy-encounter-form">
      <SteppedForm
        form={form}
        steps={steps}
        onSubmit={createSubmissionHandler(handleSubmit)}
        isSubmitting={isSubmitting}
        submitText="Update Interaction"
        onCancel={onCancel}
      >
        <FormStep id="type">
          <EncounterTypeSection
            value={form.values["encounter-type"]}
            hasLegacyEncounterType={true}
            allEncounterTypeOptions={ALL_TYPE_OPTIONS}
            currentEncounterTypeOptions={[]}
            error={form.errors["encounter-type"] as string | undefined}
            readOnly={true}
            onChange={() => undefined}
          />
        </FormStep>

        <FormStep id="notes">
          <GeneralNoteStep form={form} required={false} />
          <ClinicalNotesFields
            encounterType={form.values["encounter-type"]}
            values={form.values as unknown as Record<string, string>}
            onChange={(key, value) =>
              form.setFieldValue(key as never, value as never)
            }
          />
        </FormStep>

        <FormStep id="details">
          <EncounterDetailsStep
            form={form}
            methodOptions={METHOD_OPTIONS}
            directionOptions={DIRECTION_OPTIONS}
            billableOptions={BILLABLE_OPTIONS}
            requiresBillable={true}
          />
        </FormStep>
      </SteppedForm>

      <ConfirmationModal />
    </div>
  );
});
